import { ClientListItem } from 'src/admin/users/types';
import { Family } from './hooks/useFamily';

// Legacy families use mainFamilyMember + secondaryFamilyMembers; role-model
// families keep every member in `members` with no secondary rows. `members` is
// the source of truth for membership, so it is always unioned in. This is
// additive for legacy star families (their members are already covered by the
// primary and secondary rows), and surfaces role-model members regardless of
// the role-model flag.
export function getFamilyMemberClientIds(family: Family): string[] {
  const ids = new Set<string>();
  if (family.mainFamilyMember?.clientId) ids.add(family.mainFamilyMember.clientId);
  family.secondaryFamilyMembers.forEach(m => ids.add(m.clientId));
  family.members.forEach(m => ids.add(m.clientId));
  return [...ids];
}

export function formatFamilyMembers(
  family: Family,
  clientMap: Record<string, ClientListItem>,
): string {
  const memberIds = getFamilyMemberClientIds(family);
  const primaryId = family.mainFamilyMember?.clientId ?? memberIds[0];
  const primary = primaryId ? clientMap[primaryId] : undefined;
  if (!primary) return `${memberIds.length} members`;

  const primaryFullName = `${primary.firstName} ${primary.lastName}`;
  const otherIds = memberIds.filter(id => id !== primaryId);

  if (otherIds.length === 0) return primaryFullName;

  const firstOther = clientMap[otherIds[0]] as ClientListItem | undefined;

  let shownText: string;
  let othersCount: number;

  if (firstOther && firstOther.lastName === primary.lastName) {
    shownText = `${primary.firstName} & ${firstOther.firstName} ${primary.lastName}`;
    othersCount = otherIds.length - 1;
  } else {
    shownText = primaryFullName;
    othersCount = otherIds.length;
  }

  if (othersCount === 0) return shownText;
  return `${shownText}, and ${othersCount} other${othersCount === 1 ? '' : 's'}`;
}

/**
 * The label for a member's place in the family: their role in a role-model family,
 * their relationship to the main member in a legacy star family. Null when the family
 * records neither, leaving the fallback to the caller.
 */
export function familyRoleLabel(
  family: Family | null,
  roleNameById: Record<string, string>,
  clientId: string,
): string | null {
  if (!family) return null;

  // Role model: the role lives on the members entry. In the star model that same
  // entry is a role-less peer edge, so only treat it as authoritative when it
  // actually carries a role id.
  const member = family.members.find(m => m.clientId === clientId);
  if (member?.familyRoleId && roleNameById[member.familyRoleId]) {
    return roleNameById[member.familyRoleId];
  }

  if (family.mainFamilyMember.clientId === clientId) {
    const roleId = family.mainFamilyMember.familyRoleId;
    if (roleId && roleNameById[roleId]) return roleNameById[roleId];
  }

  // Star model: the label is the relationship to the main family member.
  const secondary = family.secondaryFamilyMembers.find(s => s.clientId === clientId);
  return secondary?.relationshipToMainFamilyMember ?? null;
}

/** Whether the family lists this client at all, under either shape. */
export function isFamilyMember(family: Family | null, clientId: string): boolean {
  if (!family) return false;
  return getFamilyMemberClientIds(family).includes(clientId);
}
